/**
 * Copy-to-clipboard, CSV and Excel download for the detail tables.
 *
 * All three take the same rows and columns the table is rendering, so what you export
 * is what you were looking at — filters applied, sort applied, nothing silently
 * different.
 */

export interface ExportColumn<T> {
  header: string
  /** The exported value. Keep it raw — formatting belongs on screen, not in a CSV. */
  value: (row: T) => string | number | null
  /**
   * Excel only: a place in the same workbook the cell links to, as `'Sheet'!A12`, or
   * null for no link on this row. Copy and CSV ignore it.
   */
  link?: (row: T) => string | null
}

const cell = (v: string | number | null): string =>
  v === null || v === undefined ? '' : String(v)

/** Tab-separated, which is what a spreadsheet expects from the clipboard. */
export function toTSV<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const head = columns.map((c) => c.header).join('\t')
  const body = rows.map((r) => columns.map((c) => cell(c.value(r))).join('\t'))
  return [head, ...body].join('\n')
}

/** RFC-4180-ish: quote anything containing a comma, quote or newline; double inner quotes. */
export function toCSV<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const esc = (v: string | number | null) => {
    const s = cell(v)
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const head = columns.map((c) => esc(c.header)).join(',')
  const body = rows.map((r) => columns.map((c) => esc(c.value(r))).join(','))
  return [head, ...body].join('\r\n')
}

export type ExportResult = 'ok' | 'blocked'

export async function copyRows<T>(
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): Promise<ExportResult> {
  try {
    await navigator.clipboard.writeText(toTSV(rows, columns))
    return 'ok'
  } catch {
    return 'blocked'
  }
}

/**
 * Saves a CSV.
 *
 * Returns 'blocked' rather than failing silently when the download cannot start — a
 * sandboxed frame without `allow-downloads` refuses it, which is exactly what happens
 * when this app is viewed inside an embedded preview. The caller tells the user to use
 * Copy instead.
 */
export function downloadCSV<T>(
  filename: string,
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): ExportResult {
  try {
    const blob = new Blob(['﻿' + toCSV(rows, columns)], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoke on the next tick; revoking synchronously can cancel the download.
    setTimeout(() => URL.revokeObjectURL(url), 0)
    return 'ok'
  } catch {
    return 'blocked'
  }
}

/* ------------------------------------------------------------------- Excel */

/**
 * A real .xlsx, written by hand.
 *
 * The alternative to ~90 lines here is a spreadsheet dependency, and the alternative
 * to that is shipping a CSV with an .xls extension — which Excel opens behind a
 * "the file format does not match" warning and which loses every number's type. The
 * scorecard export lands in front of people defending a review, so the file should
 * open without a scary dialog and should sort numerically.
 *
 * A .xlsx is a ZIP of XML. Entries are STORED, not deflated: no compression means no
 * deflate implementation, and these files are small.
 */
const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[i] = c >>> 0
  }
  return t
})()

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

interface ZipEntry {
  name: string
  data: Uint8Array
}

function zip(entries: ZipEntry[]): Blob {
  const chunks: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0
  const enc = new TextEncoder()

  const u16 = (v: number) => [v & 0xff, (v >>> 8) & 0xff]
  const u32 = (v: number) => [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff]

  for (const e of entries) {
    const name = enc.encode(e.name)
    const crc = crc32(e.data)
    const local = Uint8Array.from([
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0),
      // DOS time/date. A fixed 1980-01-01 keeps the file byte-identical between
      // runs, which matters more here than recording when someone clicked export.
      ...u16(0), ...u16(0x21),
      ...u32(crc), ...u32(e.data.length), ...u32(e.data.length),
      ...u16(name.length), ...u16(0), ...name,
    ])
    chunks.push(local, e.data)

    central.push(
      Uint8Array.from([
        ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0),
        ...u16(0), ...u16(0x21),
        ...u32(crc), ...u32(e.data.length), ...u32(e.data.length),
        ...u16(name.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
        ...u32(0), ...u32(offset), ...name,
      ]),
    )
    offset += local.length + e.data.length
  }

  const dir = central.reduce((a, b) => a + b.length, 0)
  const end = Uint8Array.from([
    ...u32(0x06054b50), ...u16(0), ...u16(0),
    ...u16(entries.length), ...u16(entries.length),
    ...u32(dir), ...u32(offset), ...u16(0),
  ])

  // Concatenated into one buffer rather than handed to Blob as a list of views:
  // `Uint8Array<ArrayBufferLike>` is not a `BlobPart` under this TS lib, and a single
  // buffer is what the ZIP offsets above already assume anyway.
  const parts = [...chunks, ...central, end]
  const out = new Uint8Array(parts.reduce((a, b) => a + b.length, 0))
  let at = 0
  for (const part of parts) {
    out.set(part, at)
    at += part.length
  }

  return new Blob([out.buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

const xmlEsc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/** One worksheet of a workbook: its tab name, rows and columns. */
export interface XLSXSheet<T> {
  name: string
  rows: readonly T[]
  columns: readonly ExportColumn<T>[]
}

export function toXLSX<T>(
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
  sheetName = 'Sheet1',
): Blob {
  return toXLSXBook([{ name: sheetName, rows, columns }])
}

/**
 * A workbook of several sheets — the Scorecard grid's every "Cells show" option in one
 * file (review #178). Tab names are cut to Excel's 31 characters, stripped of the
 * characters it refuses, and made unique.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toXLSXBook(sheets: readonly XLSXSheet<any>[]): Blob {
  const enc = new TextEncoder()
  // `linked` uses style 1 (styles.xml below): blue and underlined, as Excel draws a link.
  const cellXml = (v: string | number | null, ref: string, linked = false) => {
    if (v === null || v === undefined || v === '') return ''
    const s = linked ? ' s="1"' : ''
    return typeof v === 'number' && Number.isFinite(v)
      ? `<c r="${ref}"${s}><v>${v}</v></c>`
      : `<c r="${ref}"${s} t="inlineStr"><is><t>${xmlEsc(String(v))}</t></is></c>`
  }
  const attrEsc = (s: string) => xmlEsc(s).replace(/"/g, '&quot;')
  // A1, B1 ... AA1. The grid runs to ~20 columns, but two letters costs nothing.
  const colRef = (i: number) => {
    let s = ''
    let n = i
    do {
      s = String.fromCharCode(65 + (n % 26)) + s
      n = Math.floor(n / 26) - 1
    } while (n >= 0)
    return s
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sheetXml = ({ rows, columns }: XLSXSheet<any>) => {
    const head = `<row r="1">${columns
      .map((c, i) => cellXml(c.header, `${colRef(i)}1`))
      .join('')}</row>`
    // In-workbook links need no relationship part: a `location` is enough.
    const links: string[] = []
    const body = rows
      .map(
        (r, ri) =>
          `<row r="${ri + 2}">${columns
            .map((c, i) => {
              const ref = `${colRef(i)}${ri + 2}`
              const v = c.value(r)
              const to = c.link?.(r) ?? null
              const linked = to !== null && v !== null && v !== ''
              if (linked) links.push(`<hyperlink ref="${ref}" location="${attrEsc(to)}"/>`)
              return cellXml(v, ref, linked)
            })
            .join('')}</row>`,
      )
      .join('')
    return (
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
      `<sheetData>${head}${body}</sheetData>` +
      (links.length ? `<hyperlinks>${links.join('')}</hyperlinks>` : '') +
      `</worksheet>`
    )
  }

  // Excel refuses : \ / ? * [ ] in a tab name, more than 31 characters, and a repeat.
  const used = new Set<string>()
  const names = sheets.map((sh, i) => {
    const base = sh.name.replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || `Sheet${i + 1}`
    let name = base
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${base.slice(0, 31 - String(n).length - 1)} ${n}`
    used.add(name.toLowerCase())
    return name
  })
  const n = sheets.length

  return zip([
    {
      name: '[Content_Types].xml',
      data: enc.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
          `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
          `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
          `<Default Extension="xml" ContentType="application/xml"/>` +
          `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
          `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
          Array.from(
            { length: n },
            (_, i) =>
              `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
          ).join('') +
          `</Types>`,
      ),
    },
    {
      name: '_rels/.rels',
      data: enc.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
          `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
          `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
          `</Relationships>`,
      ),
    },
    {
      name: 'xl/workbook.xml',
      data: enc.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
          `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
          `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
          `<sheets>${names
            .map((nm, i) => `<sheet name="${xmlEsc(nm)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
            .join('')}</sheets>` +
          `</workbook>`,
      ),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: enc.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
          `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
          Array.from(
            { length: n },
            (_, i) =>
              `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
          ).join('') +
          `<Relationship Id="rId${n + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
          `</Relationships>`,
      ),
    },
    {
      // Style 0 plain, style 1 a link: Excel's own blue, underlined.
      name: 'xl/styles.xml',
      data: enc.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
          `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
          `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>` +
          `<font><u/><sz val="11"/><color rgb="FF0563C1"/><name val="Calibri"/></font></fonts>` +
          `<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>` +
          `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
          `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
          `<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
          `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>` +
          `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
          `</styleSheet>`,
      ),
    },
    ...sheets.map((sh, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: enc.encode(sheetXml(sh)) })),
  ])
}

/** Saves an .xlsx. Same 'blocked' contract as `downloadCSV`. */
export function downloadXLSX<T>(
  filename: string,
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
  sheetName?: string,
): ExportResult {
  return downloadBlob(filename, toXLSX(rows, columns, sheetName))
}

/** Saves a workbook of several sheets. Same 'blocked' contract as `downloadCSV`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function downloadXLSXBook(filename: string, sheets: readonly XLSXSheet<any>[]): ExportResult {
  return downloadBlob(filename, toXLSXBook(sheets))
}

function downloadBlob(filename: string, blob: Blob): ExportResult {
  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
    return 'ok'
  } catch {
    return 'blocked'
  }
}
